import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Area, AreaChart, CartesianGrid, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Sparkles, Loader2, Info, ChevronDown, ChevronUp } from 'lucide-react';

const fmtKr = (v) => {
  if (v == null) return '—';
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)} M kr`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)} K kr`;
  return `${Number(v).toLocaleString('sv-SE')} kr`;
};

const RevenueChartWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getRevenueChart();
        setData(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 h-72 flex items-center justify-center">
        <Loader2 size={20} className="text-[#E8613A] animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  // Slå ihop actual+forecast i en chartData där varje punkt har båda värdena
  const chartData = data.data.map(p => ({
    name: p.label,
    actual: p.is_actual ? p.revenue : null,
    forecast: !p.is_actual ? p.forecast : (p.is_actual && p.month === data.data.findIndex(d => !d.is_actual)) ? p.revenue : null,
    prev_year: p.prev_year_revenue || null,
  }));

  // Hitta brytpunkt mellan actual & forecast (sista actual-månadens label)
  const lastActualIdx = data.data.filter(d => d.is_actual).length - 1;
  const breakLabel = lastActualIdx >= 0 ? data.data[lastActualIdx].label : null;

  // Anslut linje: lägg sista actual som start för forecast också
  if (lastActualIdx >= 0 && lastActualIdx + 1 < chartData.length) {
    chartData[lastActualIdx].forecast = data.data[lastActualIdx].revenue;
  }

  const ai = data.ai_forecast;
  const hasPrevYear = (data.prev_year_total || 0) > 0;
  const yoyPct = ai?.yoy_pct;
  const confidenceColor = ai?.confidence === 'high' ? 'text-emerald-600'
    : ai?.confidence === 'low' ? 'text-amber-600' : 'text-[#E8613A]';
  const confidenceLabel = ai?.confidence === 'high' ? 'Hög säkerhet'
    : ai?.confidence === 'low' ? 'Låg säkerhet' : 'Medel säkerhet';

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="revenue-chart-widget">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#E8613A] font-bold flex items-center gap-1">
            <Sparkles size={9} /> {ai ? `AI-prognos · ${ai.model || 'GPT'}` : 'AI-prognos'}
          </p>
          <h3 className="text-base font-bold mt-0.5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Omsättning {data.year}
          </h3>
          {data.sources?.imported_fortnox > 0 && (
            <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="rev-source-info">
              Inkluderar {fmtKr(data.sources.imported_fortnox)} från Fortnox-import
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 text-right">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Hittills</p>
            <p className="text-base font-bold" data-testid="rev-ytd">{fmtKr(data.actual_ytd)}</p>
            {yoyPct != null && (
              <p className={`text-[10px] flex items-center gap-0.5 justify-end ${yoyPct >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="rev-yoy">
                {yoyPct >= 0 ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                {yoyPct >= 0 ? '+' : ''}{yoyPct.toFixed(1)}% mot i fjol
              </p>
            )}
          </div>
          <div className="border-l border-border pl-3">
            <p className="text-[10px] text-[#E8613A] uppercase">AI-prognos helår</p>
            <p className="text-base font-bold text-[#E8613A]" data-testid="rev-full-year">{fmtKr(data.full_year_estimate)}</p>
            {ai?.confidence && (
              <p className={`text-[10px] ${confidenceColor}`} data-testid="rev-confidence">{confidenceLabel}</p>
            )}
          </div>
        </div>
      </div>

      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E8613A" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#E8613A" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1A1A1A" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#1A1A1A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmtKr} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} width={50} />
            <Tooltip
              formatter={(v, name) => [fmtKr(v), name === 'prev_year' ? 'Föregående år' : name === 'actual' ? 'Faktisk' : 'AI-prognos']}
              labelStyle={{ fontWeight: 'bold', color: 'hsl(var(--foreground))' }}
              contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
            />
            {breakLabel && <ReferenceLine x={breakLabel} stroke="#E8613A" strokeDasharray="3 3" opacity={0.4} />}
            {hasPrevYear && <Line type="monotone" dataKey="prev_year" name="Föregående år" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="2 2" dot={false} />}
            <Area type="monotone" dataKey="actual" name="Faktisk" stroke="#E8613A" strokeWidth={2.5} fill="url(#actualGrad)" connectNulls={false} />
            <Area type="monotone" dataKey="forecast" name="AI-prognos" stroke="#1A1A1A" strokeWidth={2} strokeDasharray="5 4" fill="url(#forecastGrad)" connectNulls={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2.5 rounded-sm bg-[#E8613A]" /> Faktisk
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-0.5" style={{ borderTop: '2px dashed #1A1A1A' }} /> AI-prognos
        </span>
        {hasPrevYear && (
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-0.5" style={{ borderTop: '2px dashed #94a3b8' }} /> I fjol
          </span>
        )}
        <span className="ml-auto text-muted-foreground text-[10px]">
          <TrendingUp size={9} className="inline mr-1" />
          + {fmtKr(data.forecast_remaining)} förväntat
        </span>
      </div>

      {/* AI-narrativ + insights */}
      {ai?.narrative && (
        <div className="mt-3 pt-3 border-t border-border" data-testid="ai-narrative-block">
          <p className="text-xs text-foreground/90 leading-relaxed" data-testid="ai-narrative">
            <Info size={11} className="inline mr-1 text-[#E8613A]" />
            {ai.narrative}
          </p>

          {ai.insights?.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-[10px] uppercase tracking-wide text-[#E8613A] font-semibold flex items-center gap-1 hover:underline"
              data-testid="ai-insights-toggle"
            >
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              {expanded ? 'Dölj' : 'Visa'} rekommendationer ({ai.insights.length})
            </button>
          )}

          {expanded && (
            <ul className="mt-2 space-y-1.5" data-testid="ai-insights-list">
              {ai.insights.map((tip, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-[#E8613A] mt-0.5">•</span>
                  <span>{tip}</span>
                </li>
              ))}
              {ai.top_categories?.length > 0 && (
                <li className="text-[10px] text-muted-foreground pt-1.5 border-t border-border/50 mt-1.5" data-testid="ai-top-categories">
                  <strong>Topp intäkter:</strong> {ai.top_categories.slice(0, 3).map(c => `${c.category} (${fmtKr(c.amount)})`).join(' · ')}
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default RevenueChartWidget;
